import apiRoutes from '@constants/apiRoutes';
import { VisualSettings } from '@DStypes/chart';
import baseApi, { noAuthBaseApi } from '@services/api';
import { convertKeyToObject } from '@utils/common';

interface UpdateChartResponse {
  chart_title: string;
  chart_short_desc: string;
  chart_long_desc: string;
  updated_at: string;
  updated_by: number;
}

export interface EditConfigResponse {
  chart_config?: string;
  date_group?: string;
  possible_operations?: any[];
  created_at?: string;
  visual_settings?: VisualSettings | null;
  y_axis?: any;
  x_axis?: any;
}

interface UpdateChartSvgRequest {
  id: string;
  chart_svg_list: { chart_id: string; svg: string }[];
}

interface ChartInsightType {
  insights: string;
  is_active: boolean;
  created_at: string;
}

export interface ChartConfigData {
  id: string;
  chart_title: string;
  chart_project: string;
  chart_project_name: string;
  chart_short_desc: any;
  chart_long_desc: any;
  is_deleted: boolean;
  chart_reports: any[];
  has_edited_title: boolean;
  config: Config;
  created_by_auto_charts: boolean;
  chart_project_auto_charts_executed: boolean;
  svg_generated: boolean;
  colorset: string[] | null;
  project_colorset: string[] | null;
  created_at: string;
  created_by: CreatedBy;
  updated_at: string;
  newConfig: any;
  chartMatrix: any;
  chart_insights: ChartInsightType;
}

export interface Config {
  chart_type: string;
  chart_operation: string;
  chart_config: string;
  is_auto_generated: boolean;
  is_active: boolean;
  is_stacked: boolean;
  date_group: string;
  default_date_group: string;
  visual_settings: {
    '3d_charts': boolean;
    autoGroupNumbers: any;
    colorThemeToggle: boolean;
    theme_connection_status?: {
      '3d_charts': boolean;
      gradient_charts: boolean;
      multicolor_charts: boolean;
      colorThemeToggle?: boolean;
    };
    filters: any;
    filtersToggle: any;
    chartGlobalFilterToggle: boolean;
    gradient_charts: boolean;
    multicolor_charts: boolean;
    showDataLabels: boolean;
    showEmptyValues: boolean;
    showAsPercentage: boolean;
    toggleDashboardFilter: boolean;
    showLegends: boolean;
    size: any;
    sortOrder: any;
    stackChart: boolean;
    topOrBottomToggle: boolean;
    kpiTarget: number | null;
  };
  created_at: string;
  x_axis: SegmentField;
  y_axis: SegmentField;
  segment_field: SegmentField;
}

export interface SegmentField {
  id: string;
  fieldname: string;
  file_fieldname: string;
  detected_type: string;
  treat_as_type: string;
  to_import: boolean;
  contains_sensitive: boolean;
  sensitive_data_handling: null;
  is_sequence: boolean;
  segment_list: null;
  base_currency: null;
  connected_currency_field: null;
  is_segmentable: boolean;
  field_fav: boolean;
  data_facts: string;
  date_formats_found: null;
  clean_percentage: string;
  empty_rows: number;
  unique_values: number;
  file_field_type_counts: string;
  is_us_date_format: boolean;
  is_augmented: boolean;
  created_at: string;
  updated_at: string;
  datafile: string;
  created_by: number;
  updated_by: number;
}

export interface CreatedBy {
  id: number;
  is_beta: boolean;
  is_demo_user: boolean;
  email: string;
}

interface ChartDataPayload {
  x_axis: string;
  y_axis: string;
  operation: string;
  empty_values_included: string;
  percentage_format: string;
  auto_group_numbers: string;
  chart_type: string;
  is_kpi: string;
  segment_field: string;
  date_group: string;
  start_date: string;
  end_date: string;
  filters: any;
  dynamic_filters: any;
}

const chartsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCharts: builder.query<ChartConfigData[], any>({
      query: ({ id, collaboration_id }) =>
        `${apiRoutes.getCharts}/?project_id=${id}${
          collaboration_id ? `&collaboration_id=${collaboration_id}` : ''
        }`,
      transformResponse: (response: ChartConfigData[]) => {
        const transformedData = response.map((chart) => ({
          ...chart,
          config: {
            ...chart.config,
            visual_settings: {
              ...chart.config.visual_settings,
              theme_connection_status: chart?.config?.visual_settings
                ?.theme_connection_status || {
                '3d_charts': true,
                gradient_charts: true,
                multicolor_charts: true,
                colorThemeToggle: true
              }
            }
          }
        }));

        return transformedData as any;
      }
    }),
    getChartById: builder.query<ChartConfigData, any>({
      query: (id: string) => `${apiRoutes.getCharts}/${id}/`,
      transformResponse: (response) => {
        const transformedData: {
          filters?: any;
          filtersToggle?: boolean;
          chartGlobalFilterToggle?: boolean;
          sortOrder?: string;
          stackChart?: boolean;
          showDataLabels?: boolean;
          showLegends?: boolean;
          showEmptyValues?: boolean;
          showAsPercentage?: boolean;
          toggleDashboardFilter?: boolean;
          autoGroupNumbers?: boolean;
          size?: { w: number; h: number };
          [key: string]: any;
          '3d_charts'?: boolean;
          gradient_charts?: boolean;
          multicolor_charts?: boolean;
          colorThemeToggle?: boolean;
          theme_connection_status?: {
            '3d_charts': boolean;
            gradient_charts: boolean;
            multicolor_charts: boolean;
            colorThemeToggle?: boolean;
          };
          topOrBottomToggle?: boolean;
          kpiTarget?: number | null;
        } = {
          filters: undefined,
          filtersToggle: undefined,
          chartGlobalFilterToggle: undefined,
          sortOrder: undefined,
          stackChart: undefined,
          showDataLabels: undefined,
          showLegends: undefined,
          showEmptyValues: undefined,
          showAsPercentage: undefined,
          toggleDashboardFilter: undefined,
          autoGroupNumbers: undefined,
          '3d_charts': false,
          gradient_charts: false,
          multicolor_charts: false,
          colorThemeToggle: false,
          size: undefined,
          theme_connection_status: undefined,
          topOrBottomToggle: undefined,
          kpiTarget: undefined
        };

        Object.keys(transformedData).forEach((key) => {
          transformedData[key] = response?.config?.visual_settings?.[key];
        });

        if (response?.config?.chart_type === 'PIE')
          transformedData.showLegends = true;

        if (
          response?.config?.chart_type === 'PIE' &&
          transformedData.showDataLabels === undefined
        )
          transformedData.showDataLabels = true;

        response.config.visual_settings = transformedData;
        if (
          response.config.visual_settings?.theme_connection_status ===
            undefined ||
          response.config.visual_settings?.theme_connection_status === null
        )
          response.config.visual_settings.theme_connection_status = {
            '3d_charts': true,
            gradient_charts: true,
            multicolor_charts: true,
            colorThemeToggle: true
          };

        return response;
      }
    }),
    getChartByIdForInsights: builder.query<ChartConfigData, any>({
      query: (id: string) => `${apiRoutes.getCharts}/${id}/`,
      providesTags: ['basicChartData'],
      transformResponse: (response) => {
        const transformedData: {
          filters?: any;
          filtersToggle?: boolean;
          chartGlobalFilterToggle?: boolean;
          sortOrder?: string;
          stackChart?: boolean;
          showDataLabels?: boolean;
          showLegends?: boolean;
          showEmptyValues?: boolean;
          toggleDashboardFilter?: boolean;
          autoGroupNumbers?: boolean;
          size?: { w: number; h: number };
          [key: string]: any;
          '3d_charts'?: boolean;
          gradient_charts?: boolean;
          multicolor_charts?: boolean;
          colorThemeToggle?: boolean;
          theme_connection_status?: {
            '3d_charts': boolean;
            gradient_charts: boolean;
            multicolor_charts: boolean;
            colorThemeToggle?: boolean;
          };
          topOrBottomToggle?: boolean;
          kpiTarget?: number | null;
        } = {
          filters: undefined,
          filtersToggle: undefined,
          chartGlobalFilterToggle: undefined,
          sortOrder: undefined,
          stackChart: undefined,
          showDataLabels: undefined,
          showLegends: undefined,
          showEmptyValues: undefined,
          toggleDashboardFilter: undefined,
          autoGroupNumbers: undefined,
          '3d_charts': false,
          gradient_charts: false,
          multicolor_charts: false,
          colorThemeToggle: false,
          size: undefined,
          theme_connection_status: undefined,
          topOrBottomToggle: undefined,
          kpiTarget: undefined
        };

        Object.keys(transformedData).forEach((key) => {
          transformedData[key] = response?.config?.visual_settings?.[key];
        });

        if (response?.config?.chart_type === 'PIE')
          transformedData.showLegends = true;

        if (
          response?.config?.chart_type === 'PIE' &&
          transformedData.showDataLabels === undefined
        )
          transformedData.showDataLabels = true;

        response.config.visual_settings = transformedData;
        if (
          response.config.visual_settings?.theme_connection_status ===
            undefined ||
          response.config.visual_settings?.theme_connection_status === null
        )
          response.config.visual_settings.theme_connection_status = {
            '3d_charts': true,
            gradient_charts: true,
            multicolor_charts: true,
            colorThemeToggle: true
          };

        return response;
      }
    }),
    getChartDataFromConfig: builder.mutation<any, any>({
      query: ({
        id,
        collaboration_id,
        payload
      }: {
        id: string;
        collaboration_id: string;
        payload: ChartDataPayload;
      }) => ({
        url: `${apiRoutes.getCharts}/${id}/data/${
          collaboration_id ? `?collaboration_id=${collaboration_id}` : ''
        }`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['basicChartData'],
      // providesTags: ['chartData'],
      transformResponse: (response) => ({
        ...response,
        data: response.data,
        date_group: response.date_group
      })
    }),
    getChartDetails: builder.query<
      any,
      { id: string; xId?: string; yId?: string; is_kpi?: string }
    >({
      query: ({ id, xId, yId, is_kpi }) =>
        `${apiRoutes.getCharts}/details/?project_id=${id}${
          xId ? '&x_axis=' + xId : ''
        }${yId ? '&y_axis=' + yId : ''}&is_kpi=${
          is_kpi === 'True' ? 'True' : 'False'
        }`,
      transformResponse: (response) => {
        const transformedData: {
          chart_type_choices?: any[];
          chart_operation_choices?: any[];
          available_operation_choices?: any[];
          segment_fields?: any[];
          available_fields?: any[];
          [key: string]: any;
        } = {};

        Object.keys(response).forEach((key) => {
          switch (key) {
            case 'available_fields':
              transformedData[key] = response.available_fields.map(
                (item: any) => ({
                  label: item.fieldname,
                  value: item.id,
                  type: item.treat_as_type,
                  contains_sensitive: item.contains_sensitive,
                  is_segmentable: item.is_segmentable
                })
              );
              break;
            case 'segment_fields':
              transformedData[key] = response.segment_fields.map(
                (item: any) => ({
                  label: item.fieldname,
                  value: item.id
                })
              );
              break;
            case 'chart_operation_choices':
              transformedData[key] = response.chart_operation_choices.map(
                (item: string) => ({ label: item[0], value: item[0] })
              );
              break;
            case 'available_operation_choices':
              transformedData[key] = response.available_operation_choices.map(
                (item: string) => ({ label: item, value: item })
              );
              break;
            default:
              transformedData[key] = response[key].map((item: any[]) => ({
                value: item[0],
                label: item[1]
              }));
          }
        });
        transformedData.originalData = response;

        return transformedData;
      }
    }),
    getChartLink: builder.query<string, string>({
      query: (id: string) => ({
        url: `/collab/link/?chart_id=${id}`
      })
    }),
    updateEmailChartLink: builder.mutation<any, any>({
      query: ({ id, emailIds }: { id: string; emailIds: string[] }) => ({
        url: `/collab/link/email/?chart_id=${id}&email_ids=${emailIds}`,
        method: 'PUT'
      })
    }),
    deleteChartById: builder.mutation<any, any>({
      query: (id) => ({
        url: `${apiRoutes.deleteProject}/${id}/`,
        method: 'DELETE'
      })
      // transformResponse: (data: SignInResponse) => data
    }),
    updateChart: builder.mutation<UpdateChartResponse, any>({
      query: ({ id, payload }) => ({
        url: `${apiRoutes.getCharts}/${id}/`,
        method: 'PUT',
        body: payload
      })
    }),
    updateChartTheme: builder.mutation<
      any,
      { id: string; payload: { colorset: string | null } }
    >({
      query: ({ id, payload }) => ({
        url: `${apiRoutes.getCharts}/${id}/theme/`,
        method: 'PUT',
        body: payload
      }),
      transformResponse: (data: { colorset: string[] }) => data.colorset
    }),
    newChart: builder.mutation<any, any>({
      query: (payload) => ({
        url: `${apiRoutes.getCharts}/`,
        method: 'POST',
        body: payload
      })
      // transformResponse: (data: SignInResponse) => data
    }),
    updateChartSvg: builder.mutation<any, UpdateChartSvgRequest>({
      query: ({ chart_svg_list, id }: UpdateChartSvgRequest) => ({
        url: `${apiRoutes.projects}${id}/${apiRoutes.svgUpload}`,
        method: 'PUT',
        body: { chart_svg_list }
      })
      // transformResponse: (data: SignInResponse) => data
    }),
    updateChartConfig: builder.mutation<EditConfigResponse, any>({
      query: ({ id, payload }) => ({
        url: `${apiRoutes.getCharts}/${id}/config/`,
        method: 'PUT',
        body: payload
      }),
      transformResponse: (response: any) => {
        let possible_operations = null;

        if (response?.possible_operations?.length)
          possible_operations = response.possible_operations.map(
            (operation: string) => ({
              label: operation,
              value: operation
            })
          );

        return { ...response, possible_operations };
      },
      invalidatesTags: ['basicChartData']
    }),
    createChartComment: builder.mutation<any, any>({
      query: ({ id, payload }: any) => ({
        url: `collab/comment/?chart_id=${id}`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['commentList']
    }),
    getChartComments: builder.query<any, any>({
      query: (id) => `collab/comment/?chart_id=${id}`,
      providesTags: ['commentList']
    }),
    getChartMatrix: builder.query({
      query: () => 'chart/matrix/',
      transformResponse: (data) => {
        const transformedData = Object.entries(data).map(([key, value]) =>
          convertKeyToObject(key, value)
        );

        return transformedData;
      }
    }),
    updateAutoCategorization: builder.mutation({
      query: ({ id, payload }: any) => ({
        url: `chart/${id}/categorise/column/`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['basicChartData']
    }),
    updateChartGlobalFilters: builder.mutation<
      any,
      { chart_id: string; payload: any }
    >({
      query: ({ chart_id, payload }: { chart_id: string; payload: any }) => ({
        url: `chart/${chart_id}/filters/`,
        body: payload,
        method: 'PUT'
      }),
      invalidatesTags: ['basicChartData']
    }),
    updateChartDynamicFilters: builder.mutation<
      any,
      { chart_id: string; payload: any }
    >({
      query: ({ chart_id, payload }: { chart_id: string; payload: any }) => ({
        url: `chart/${chart_id}/dynamic_filters/`,
        body: payload,
        method: 'PUT'
      }),
      invalidatesTags: ['basicChartData']
    }),
    updateChartInsights: builder.mutation<
      any,
      { chart_id: string; payload: any }
    >({
      query: ({ chart_id, payload }: { chart_id: string; payload: any }) => ({
        url: `chart/${chart_id}/insight/`,
        body: payload,
        method: 'PUT'
      })
    })
  })
});

export const {
  useLazyGetChartsQuery,
  useDeleteChartByIdMutation,
  useGetChartDetailsQuery,
  useLazyGetChartLinkQuery,
  useUpdateEmailChartLinkMutation,
  useUpdateChartMutation,
  useGetChartByIdQuery,
  useNewChartMutation,
  useLazyGetChartDetailsQuery,
  useUpdateChartConfigMutation,
  useCreateChartCommentMutation,
  useGetChartCommentsQuery,
  useUpdateChartThemeMutation,
  useUpdateChartSvgMutation,
  useGetChartMatrixQuery,
  useUpdateAutoCategorizationMutation,
  useUpdateChartGlobalFiltersMutation,
  useUpdateChartDynamicFiltersMutation,
  useUpdateChartInsightsMutation,
  useGetChartByIdForInsightsQuery,
  useLazyGetChartByIdForInsightsQuery,
  useGetChartDataFromConfigMutation
} = chartsApi;

const chartPreviewApi: any = noAuthBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPreviewChartById: builder.query<any, any>({
      query: ({ id, collaboration_id }) =>
        `${apiRoutes.getCharts}/${id}/?collaboration_id=${collaboration_id}`
    }),
    getPreviewChartDataFromConfig: builder.query<any, any>({
      query: ({
        id,
        collaboration_id,
        payload
      }: {
        id: string;
        collaboration_id: string;
        payload: ChartDataPayload;
      }) => ({
        url: `${apiRoutes.getCharts}/${id}/data/${
          collaboration_id ? `?collaboration_id=${collaboration_id}` : ''
        }`,
        method: 'POST',
        body: payload
      }),
      transformResponse: (response: any) => ({
        data: response.data,
        date_group: response.date_group
      })
    }),
    getPreviewChartComments: builder.query<any, any>({
      query: (id) => `collab/comment/?chart_id=${id}`
    }),
    createPreviewChartComment: builder.mutation<any, any>({
      query: ({ id, payload }: any) => ({
        url: `collab/comment/?chart_id=${id}`,
        method: 'POST',
        body: payload
      })
    })
  })
});

export const {
  useGetPreviewChartByIdQuery,
  useLazyGetPreviewChartDataFromConfigQuery,
  useLazyGetPreviewChartCommentsQuery,
  useCreatePreviewChartCommentMutation
} = chartPreviewApi;
